import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  criarUsuario,
  atualizarUsuario,
  Usuario,
  UsuarioInput,
} from '../../services/usuarioService';
import {
  adminUsuarioCreateSchema,
  adminUsuarioEditSchema,
  AdminUsuarioCreateFormValues,
} from '../../schemas/usuarioSchemas';

export interface AdminUsuarioModalProps {
  open: boolean;
  onClose: () => void;
  usuario?: Usuario;
  onSaved?: (usuario: Usuario) => void;
}

const AdminUsuarioModal: React.FC<AdminUsuarioModalProps> = ({
  open,
  onClose,
  usuario,
  onSaved,
}) => {
  const isEdit = Boolean(usuario);
  const schema = isEdit ? adminUsuarioEditSchema : adminUsuarioCreateSchema;

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<AdminUsuarioCreateFormValues>({ resolver: zodResolver(schema) });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (usuario) {
        reset({
          nome: usuario.nome || '',
          email: usuario.email || '',
          username: usuario.username || '',
          senha: '',
          ativo: typeof usuario.ativo === 'boolean' ? usuario.ativo : true,
        });
      } else {
        reset({
          nome: '',
          email: '',
          username: '',
          senha: '',
          ativo: true,
        });
      }
      setErrorMsg(null);
      setTimeout(() => setFocus('nome'), 0);
    }
  }, [open, usuario, reset, setFocus]);

  if (!open) return null;

  const onSubmit = async (values: AdminUsuarioCreateFormValues) => {
    setErrorMsg(null);
    try {
      if (usuario) {
        const input: UsuarioInput = {
          nome: values.nome,
          email: values.email,
          username: values.username,
          ativo: values.ativo,
          role: 'ADMIN',
        };
        const saved = await atualizarUsuario(usuario.id, input);
        onSaved?.(saved);
      } else {
        const input: UsuarioInput = {
          nome: values.nome,
          email: values.email,
          username: values.username,
          senha: values.senha,
          ativo: values.ativo,
          role: 'ADMIN',
        };
        const saved = await criarUsuario(input);
        onSaved?.(saved);
      }
      onClose();
    } catch (e) {
      setErrorMsg(usuario ? 'Erro ao atualizar usuário.' : 'Erro ao criar usuário.');
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" role="dialog" tabIndex={-1}>
        <div className="modal-dialog" role="document">
          <form className="modal-content" onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">
                {usuario ? 'Editar Admin' : 'Novo Admin'}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="modal-body">
              {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
              <div className="mb-3">
                <label className="form-label">Nome*</label>
                <input
                  className="form-control"
                  aria-label="Nome"
                  {...register('nome')}
                  disabled={isSubmitting}
                />
                {errors.nome && (
                  <span className="text-danger small">{errors.nome.message}</span>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Email*</label>
                <input
                  type="email"
                  className="form-control"
                  aria-label="Email"
                  {...register('email')}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <span className="text-danger small">{errors.email.message}</span>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Username*</label>
                <input
                  className="form-control"
                  aria-label="Username"
                  {...register('username')}
                  disabled={isSubmitting}
                />
                {errors.username && (
                  <span className="text-danger small">{errors.username.message}</span>
                )}
              </div>
              {!usuario && (
                <div className="mb-3">
                  <label className="form-label">Senha*</label>
                  <input
                    type="password"
                    className="form-control"
                    aria-label="Senha"
                    {...register('senha')}
                    disabled={isSubmitting}
                  />
                  {errors.senha && (
                    <span className="text-danger small">{errors.senha.message}</span>
                  )}
                </div>
              )}
              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="adminAtivo"
                    {...register('ativo')}
                    disabled={isSubmitting}
                  />
                  <label className="form-check-label ps-1" htmlFor="adminAtivo">
                    Ativo
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AdminUsuarioModal;
